import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { useTranslation } from 'react-i18next'

export interface DocumentationContentConfirmDialogState {
  type:
    | 'publish'
    | 'archive'
    | 'set-default'
    | 'delete'
    | 'quick-sync'
    | 'rebuild-sync'
    | 'force-published-sync'
  versionCode: string
  setDefault?: boolean
  requiresReplacement?: boolean
  replacementVersionCode: string
  confirmVersionCode: string
  deleteReason: string
}

interface DocumentationContentConfirmDialogProps {
  dialog: DocumentationContentConfirmDialogState | null
  publishedVersionCodes: string[]
  isLoading: boolean
  onClose: () => void
  onConfirm: () => void
  onUpdateDialog: (
    updater: (current: DocumentationContentConfirmDialogState | null) => DocumentationContentConfirmDialogState | null
  ) => void
}

export function DocumentationContentConfirmDialog({
  dialog,
  publishedVersionCodes,
  isLoading,
  onClose,
  onConfirm,
  onUpdateDialog,
}: DocumentationContentConfirmDialogProps) {
  const { t } = useTranslation()

  const title = (() => {
    switch (dialog?.type) {
      case 'publish':
        return t('documentationContent.confirm.publishTitle')
      case 'set-default':
        return t('documentationContent.confirm.defaultSwitchTitle')
      case 'archive':
        return t('documentationContent.confirm.archiveTitle')
      case 'delete':
        return t('documentationContent.confirm.deleteTitle')
      case 'quick-sync':
        return t('documentationContent.confirm.quickSyncTitle')
      case 'force-published-sync':
        return t('documentationContent.confirm.forcePublishedTitle')
      case 'rebuild-sync':
      default:
        return t('documentationContent.confirm.rebuildTitle')
    }
  })()

  return (
    <Modal isOpen={Boolean(dialog)} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {dialog?.type === 'publish' ? (
            <VStack align="stretch" spacing={3}>
              <Text>
                {dialog.setDefault
                  ? t('documentationContent.confirm.publishAndDefaultMessage', {
                      versionCode: dialog.versionCode,
                    })
                  : t('documentationContent.confirm.publishMessage', {
                      versionCode: dialog.versionCode,
                    })}
              </Text>
              {dialog.setDefault ? (
                <Text fontSize="sm" color="gray.500">
                  {t('documentationContent.confirm.publishDefaultHint')}
                </Text>
              ) : null}
            </VStack>
          ) : dialog?.type === 'set-default' ? (
            <VStack align="stretch" spacing={3}>
              <Text>
                {t('documentationContent.confirm.setDefaultMessage', {
                  versionCode: dialog.versionCode,
                })}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {t('documentationContent.confirm.setDefaultHint')}
              </Text>
            </VStack>
          ) : dialog?.type === 'archive' ? (
            <VStack align="stretch" spacing={3}>
              <Text>
                {t('documentationContent.confirm.archiveMessage', {
                  versionCode: dialog.versionCode,
                })}
              </Text>
              {dialog.requiresReplacement ? (
                <FormControl>
                  <FormLabel mb={1}>
                    {t('documentationContent.confirm.replacementVersionCode')}
                  </FormLabel>
                  <DropdownSelect
                    size="sm"
                    value={dialog.replacementVersionCode}
                    placeholder={t('documentationContent.confirm.selectReplacementVersion')}
                    options={publishedVersionCodes
                      .filter((code) => code !== dialog.versionCode)
                      .map((code) => ({
                        value: code,
                        label: code,
                      }))}
                    onChange={(value) => {
                      onUpdateDialog((current) => {
                        if (!current || current.type !== 'archive') {
                          return current
                        }
                        return {
                          ...current,
                          replacementVersionCode: value,
                        }
                      })
                    }}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {t('documentationContent.confirm.archiveReplacementHint')}
                  </Text>
                </FormControl>
              ) : null}
            </VStack>
          ) : dialog?.type === 'delete' ? (
            <VStack align="stretch" spacing={3}>
              <Text>
                {t('documentationContent.confirm.deleteMessage', {
                  versionCode: dialog.versionCode,
                })}
              </Text>
              <Text fontSize="sm" color="red.500">
                {t('documentationContent.confirm.deleteCannotUndo')}
              </Text>
              {dialog.requiresReplacement ? (
                <FormControl>
                  <FormLabel mb={1}>
                    {t('documentationContent.confirm.replacementVersionCode')}
                  </FormLabel>
                  <DropdownSelect
                    size="sm"
                    value={dialog.replacementVersionCode}
                    placeholder={t('documentationContent.confirm.selectReplacementVersion')}
                    options={publishedVersionCodes
                      .filter((code) => code !== dialog.versionCode)
                      .map((code) => ({
                        value: code,
                        label: code,
                      }))}
                    onChange={(value) => {
                      onUpdateDialog((current) => {
                        if (!current || (current.type !== 'delete' && current.type !== 'archive')) {
                          return current
                        }
                        return {
                          ...current,
                          replacementVersionCode: value,
                        }
                      })
                    }}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {t('documentationContent.confirm.deleteReplacementHint')}
                  </Text>
                </FormControl>
              ) : null}
              <FormControl>
                <FormLabel mb={1}>{t('documentationContent.confirm.typeVersionCode')}</FormLabel>
                <Input
                  value={dialog.confirmVersionCode}
                  onChange={(event) => {
                    const value = event.target.value
                    onUpdateDialog((current) => {
                      if (!current || current.type !== 'delete') {
                        return current
                      }
                      return {
                        ...current,
                        confirmVersionCode: value,
                      }
                    })
                  }}
                  placeholder={dialog.versionCode}
                />
              </FormControl>
              <FormControl>
                <FormLabel mb={1}>{t('documentationContent.confirm.reasonOptional')}</FormLabel>
                <Input
                  value={dialog.deleteReason}
                  onChange={(event) => {
                    const value = event.target.value
                    onUpdateDialog((current) => {
                      if (!current || current.type !== 'delete') {
                        return current
                      }
                      return {
                        ...current,
                        deleteReason: value,
                      }
                    })
                  }}
                  placeholder={t('documentationContent.confirm.reasonPlaceholder')}
                />
              </FormControl>
            </VStack>
          ) : dialog?.type === 'quick-sync' ? (
            <VStack align="stretch" spacing={3}>
              <Text>{t('documentationContent.confirm.quickSyncMessage')}</Text>
              <Text fontSize="sm" color="gray.500">
                {t('documentationContent.confirm.quickSyncPurpose')}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {t('documentationContent.confirm.quickSyncRecommendation')}
              </Text>
            </VStack>
          ) : dialog?.type === 'force-published-sync' ? (
            <VStack align="stretch" spacing={3}>
              <Text>
                {t('documentationContent.confirm.forcePublishedMessage', {
                  versionCode: dialog.versionCode,
                })}
              </Text>
              <Text fontSize="sm" color="red.500">
                {t('documentationContent.confirm.forcePublishedWarning')}
              </Text>
              <FormControl>
                <FormLabel mb={1}>{t('documentationContent.confirm.typeVersionCode')}</FormLabel>
                <Input
                  value={dialog.confirmVersionCode}
                  onChange={(event) => {
                    const value = event.target.value
                    onUpdateDialog((current) => {
                      if (!current || current.type !== 'force-published-sync') {
                        return current
                      }
                      return {
                        ...current,
                        confirmVersionCode: value,
                      }
                    })
                  }}
                  placeholder={dialog.versionCode}
                />
              </FormControl>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={3}>
              <Text>{t('documentationContent.confirm.rebuildMessage')}</Text>
              <Text fontSize="sm" color="gray.500">
                {t('documentationContent.confirm.rebuildPurpose')}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {t('documentationContent.confirm.rebuildUsage')}
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme={
                dialog?.type === 'archive' || dialog?.type === 'delete'
                  ? 'red'
                  : dialog?.type === 'force-published-sync'
                    ? 'red'
                  : dialog?.type === 'rebuild-sync'
                    ? 'red'
                    : dialog?.type === 'set-default'
                      ? 'teal'
                      : 'blue'
              }
              onClick={onConfirm}
              isLoading={isLoading}
              isDisabled={
                (
                  dialog?.type === 'archive'
                  && Boolean(dialog?.requiresReplacement)
                  && !dialog?.replacementVersionCode.trim()
                )
                || (
                  dialog?.type === 'delete'
                  && (
                    dialog.confirmVersionCode.trim() !== dialog.versionCode
                    || (
                      Boolean(dialog.requiresReplacement)
                      && !dialog.replacementVersionCode.trim()
                    )
                  )
                )
                || (
                  dialog?.type === 'force-published-sync'
                  && dialog.confirmVersionCode.trim() !== dialog.versionCode
                )
              }
            >
              {t('common.confirm')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
